/*
 * Marketplace specific configuration.
 *
 * Every filter needs to have following keys:
 * - id:     Unique id of the filter.
 * - label:  The default label of the filter.
 * - type:   String that represents one of the existing filter components:
 *           BookingDateRangeFilter, KeywordFilter, PriceFilter,
 *           SelectSingleFilter, SelectMultipleFilter.
 * - group:  Is this 'primary' or 'secondary' filter?
 *           Primary filters are visible on desktop layout by default.
 *           Secondary filters are behind "More filters" button.
 *           Read more from src/containers/SearchPage/README.md
 * - queryParamNames: Describes parameters to be used with queries
 *                    (e.g. 'price' or 'pub_amenities'). Most of these are
 *                    the same between webapp URLs and API query params.
 *                    You can't change 'dates', 'price', or 'keywords'
 *                    since those filters are fixed to a specific attribute.
 * - config: Extra configuration that the filter component needs.
 *
 * Note 1: Labels could be tied to translation file
 *         by importing FormattedMessage:
 *         <FormattedMessage id="some.translation.key.here" />
 *
 * Note 2: If you need to add new custom filter components,
 *         you need to take those into use in:
 *         src/containers/SearchPage/FilterComponent.js
 *
 * Note 3: If you just want to create more enum filters
 *         (i.e. SelectSingleFilter, SelectMultipleFilter),
 *         you can just add more configurations with those filter types
 *         and tie them with correct extended data key
 *         (i.e. pub_<key> or meta_<key>).
 */

export const OTHER_OPTION = 'other';

export const COACHING_FORMAT_FILTER_ID = 'coachingFormat';
export const LANGUAGES_FILTER_ID = 'languages';
export const COACHING_STYLES_FILTER_ID = 'coachingStyles';
export const DEVELOPMENT_SITUATIONS_FILTER_ID = 'developmentSituations';
export const INDUSTRIES_FILTER_ID = 'industries';
export const COACHING_ACCREDITATIONS_FILTER_ID = 'coachingAccreditations';
export const COACHING_EXPERIENCE_BY_CAREER_PHASE_FILTER_ID = 'coachingExperienceByCareerPhase';
export const HR_STRATEGIC_SKILLS_FILTER_ID = 'hrStrategicSkills';
export const ASSESSMENT_ACCREDITATIONS_FILTER_ID = 'assessmentAccreditations';
export const PAYMENT_METHODS_FILTER_ID = 'paymentMethods';

export const EMPTY_ROLE_ROW = { jobTitle: '', company: '', dates: '' };
export const EMPTY_CULTURAL_EXPOSURE_ROW = { countryRegion: '', dates: '' };

export const filters = [
  {
    id: LANGUAGES_FILTER_ID,
    label: 'Languages (Business Level Proficiency)',
    type: 'SelectMultipleFilter',
    group: 'primary',
    queryParamNames: ['pub_languages'],
    config: {
      schemaType: 'multi-enum',
      searchMode: 'has_any',
      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: 'english', label: 'English' },
        { key: 'amharic', label: 'Amharic' },
        { key: 'arabic', label: 'Arabic' },
        { key: 'bengali', label: 'Bengali' },
        { key: 'cantonese', label: 'Chinese - Cantonese' },
        { key: 'mandarin', label: 'Chinese - Mandarin' },
        { key: 'czechHungarian', label: 'Czech / Hungarian' },
        { key: 'danishSwedishNorwegian', label: 'Danish / Swedish / Norwegian' },
        { key: 'dutch', label: 'Dutch' },
        { key: 'filipino', label: 'Filipino (Tagalog)' },
        { key: 'french', label: 'French' },
        { key: 'german', label: 'German' },
        { key: 'greek', label: 'Greek' },
        { key: 'hausa', label: 'Hausa' },
        { key: 'hebrew', label: 'Hebrew' },
        { key: 'hindi', label: 'Hindi' },
        { key: 'italian', label: 'Italian' },
        { key: 'japanese', label: 'Japanese' },
        { key: 'javanese', label: 'Javanese / Indonesian' },
        { key: 'korean', label: 'Korean' },
        { key: 'malay', label: 'Malay' },
        { key: 'marathi', label: 'Marathi' },
        { key: 'persianFarsi', label: 'Persian (Farsi)' },
        { key: 'polish', label: 'Polish' },
        { key: 'portuguese', label: 'Portuguese' },
        { key: 'punjabi', label: 'Punjabi' },
        { key: 'russian', label: 'Russian' },
        { key: 'spanish', label: 'Spanish' },
        { key: 'swahili', label: 'Swahili' },
        { key: 'tamil', label: 'Tamil' },
        { key: 'telugu', label: 'Telugu' },
        { key: 'thai', label: 'Thai' },
        { key: 'ukrainian', label: 'Ukrainian' },
        { key: 'urdu', label: 'Urdu' },
        { key: 'vietnamese', label: 'Vietnamese' },
        { key: 'yorubaIgbo', label: 'Yoruba / Igbo' },
        { key: OTHER_OPTION, label: 'Other' },
      ],
      isUseCustomFilterPopup: true,
    },
  },
  {
    id: 'price',
    label: 'Rate',
    type: 'PriceFilter',
    group: 'primary',
    // Note: PriceFilter is fixed filter,
    // you can't change "queryParamNames: ['price'],"
    queryParamNames: ['pub_minPriceAmount'],
    // Price filter configuration
    // Note: unlike most prices this is not handled in subunits
    config: {
      min: 100,
      max: 2000,
      step: 5,
    },
  },
  {
    id: INDUSTRIES_FILTER_ID,
    label: 'Industries',
    type: 'SelectMultipleFilter',
    group: 'primary',
    queryParamNames: ['pub_industries'],
    config: {
      schemaType: 'multi-enum',
      searchMode: 'has_any',
      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: 'accommodationandFoodServices', label: 'Accommodation & Food Services' },
        { key: 'agileAgriculture', label: 'Agriculture, Forestry & Fishing' },
        { key: 'businessFranchises', label: 'Business Franchises' },
        { key: 'constructionProperty', label: 'Construction & Property' },
        { key: 'defenceAerospace', label: 'Defence & Aerospace' },
        { key: 'educationHigherEducation', label: 'Education - Higher Education' },
        { key: 'education', label: 'Education - Primary & Secondary' },
        { key: 'electricity', label: 'Electricity, Gas, Water & Waste Services' },
        { key: 'engineering', label: 'Engineering' },
        { key: 'familyOffice', label: 'Family Office' },
        { key: 'financialServices', label: 'Financial Services' },
        { key: 'fmcg', label: 'FMCG' },
        { key: 'government', label: 'Government' },
        { key: 'healthcare', label: 'Healthcare' },
        { key: 'hospitals', label: 'Hospitals' },
        { key: 'industrial', label: 'Industrial' },
        { key: 'legalFirms', label: 'Legal Firms' },
        { key: 'lifeSciences', label: 'Life Sciences, Medical Devices & Pharma' },
        { key: 'luxuryServicesRetail', label: 'Luxury Services & Retail' },
        { key: 'manufacturing', label: 'Manufacturing' },
        { key: 'informationMedia', label: 'Media & Telecommunications' },
        { key: 'notForProfit', label: 'Not-for-Profit' },
        { key: 'oilGas', label: 'Oil & Gas' },
        { key: 'personalServices', label: 'Personal Services' },
        { key: 'professionalServices', label: 'Professional Services' },
        { key: 'recreationServices', label: 'Recreation Services' },
        { key: 'retailTrade', label: 'Retail Trade - Online/Offline' },
        { key: 'socialServicesCommunityServices', label: 'Social Services / Community Services' },
        { key: 'sporting', label: 'Sporting' },
        { key: 'technologySoftwareSaaS', label: 'Technology / Software / SaaS' },
        { key: 'theArts', label: 'The Arts' },
        { key: 'transport', label: 'Transport, Shipping & Logistics' },
        { key: 'startUp', label: 'VC-backed Start-up' },
        { key: 'wholesaleTrade', label: 'Wholesale Trade' },
        { key: OTHER_OPTION, label: 'Other' },
      ],
      isUseCustomFilterPopup: true,
    },
  },
  {
    id: 'location',
    label: 'Location',
    type: 'LocationFilter',
    group: 'primary',
    queryParamNames: ['address', 'bounds', 'origin'],
    config: {},
  },
  {
    id: COACHING_FORMAT_FILTER_ID,
    label: 'Format',
    type: 'SelectMultipleFilter',
    group: 'primary',
    queryParamNames: ['pub_coachingFormat'],
    config: {
      schemaType: 'multi-enum',
      searchMode: 'has_any',
      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [{ key: 'faceToFace', label: 'Face to face' }, { key: 'online', label: 'Online' }],
    },
  },
  {
    id: COACHING_STYLES_FILTER_ID,
    label: 'Coaching Styles / Formats',
    type: 'SelectMultipleFilter',
    group: 'secondary',
    queryParamNames: ['pub_coachingStyles'],
    config: {
      schemaType: 'multi-enum',
      searchMode: 'has_any',
      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: 'agileCoaching', label: 'Agile Coaching' },
        { key: 'leadershipCoaching', label: '1:1 Leadership coaching' },
        { key: 'augmentedLiveAICoaching', label: 'Augmented live+AI coaching' },
        { key: 'businessCoaching', label: 'Business Coaching' },
        { key: 'cognitiveBehavioural', label: 'Cognitive Behavioural' },
        { key: 'crossCulturalCoaching', label: 'Cross-cultural coaching' },
        { key: 'eltBoardLevelCoaching', label: 'ELT / Board Level Coaching' },
        { key: 'groupCoaching', label: 'Group Coaching' },
        { key: 'groupRelations', label: 'Group Relations' },
        { key: 'laserOnDemandCoaching', label: 'Laser/On-Demand Coaching' },
        { key: 'lifeCoaching', label: 'Life Coaching' },
        { key: 'mentoring', label: 'Mentoring' },
        { key: 'neurodivergentCoaching', label: 'Neurodivergent Coaching' },
        { key: 'neuroscienceInformed', label: 'Neuroscience-Informed' },
        { key: 'ontological', label: 'Ontological' },
        { key: 'presentationSkillsCoaching', label: 'Presentation Skills Coaching' },
        { key: 'psychodynamic', label: 'Psychodynamic' },
        { key: 'somaticCoaching', label: 'Somatic Coaching' },
        { key: 'strategyCoaching', label: 'Strategy Coaching' },
        { key: 'systemsCoaching', label: 'Systems Coaching' },
        { key: 'teamCoaching', label: 'Team Coaching' },
        { key: 'theatreLed', label: 'Theatre-led' },
        { key: 'traumaInformed', label: 'Trauma-Informed' },
        { key: OTHER_OPTION, label: 'Other Coaching Format' },
      ],
    },
  },
  {
    id: DEVELOPMENT_SITUATIONS_FILTER_ID,
    label: 'Development Situations',
    type: 'SelectMultipleFilter',
    group: 'secondary',
    queryParamNames: ['pub_developmentSituations'],
    config: {
      schemaType: 'multi-enum',
      searchMode: 'has_any',
      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: 'boardDevelopment', label: 'Board Development' },
        { key: 'burnoutPreventionRecovery', label: 'Burnout Prevention / Recovery' },
        { key: 'careerTransitions', label: 'Career Transitions' },
        { key: 'communicationSkills', label: 'Communication Skills' },
        { key: 'confidenceAndInfluence', label: 'Confidence & Influence' },
        { key: 'conflictManagement', label: 'Conflict Management' },
        { key: 'crossCulturalCoaching', label: 'Cross-cultural Leadership' },
        { key: 'cultureTransitions', label: 'Culture Secondment / Transitions' },
        { key: 'cxoFortune500', label: 'CXO Fortune 500 Development' },
        { key: 'definingPurposeMotivation', label: 'Defining Purpose & Motivation' },
        { key: 'deiProgramInitiatives', label: 'DEI Program Initiatives' },
        { key: 'downsizingOutsourcing', label: 'Downsizing / Outsourcing' },
        { key: 'earlyCareers', label: 'Early Careers' },
        { key: 'entrepreneurialSupport', label: 'Entrepreneurial Support' },
        { key: 'partnerDevelopment', label: 'Equity Partner Pathways' },
        { key: 'executivePresence', label: 'Executive Presence' },
        { key: 'firstTimeManagerTransition', label: 'First-Time Manager Transition' },
        { key: 'founderTransitions', label: 'Founder Transitions' },
        { key: 'habitChange', label: 'Habit Change' },
        { key: 'highPotentialPrograms', label: 'High-Potential Programs' },
        { key: 'leadershipDevelopment', label: 'Leadership Development' },
        { key: 'leadershipOnboarding', label: 'Leadership Onboarding' },
        { key: 'leadingThroughChange', label: 'Leading Through Change' },
        { key: 'managementDevelopment', label: 'Management Development' },
        { key: 'menopause', label: 'Menopause Support' },
        { key: 'organisationalNavigation', label: 'Organisational Navigation' },
        { key: 'parentalTransitions', label: 'Parental Transitions' },
        { key: 'performanceEffectiveness', label: 'Performance & Productivity' },
        { key: 'impactConfidence', label: 'Personal Impact / Profile Raising' },
        { key: 'redundancyExitTransitions', label: 'Redundancy / Exit Transitions' },
        { key: 'relationshipDevelopment', label: 'Relationship Development' },
        { key: 'remoteTeamManagement', label: 'Remote Team Management' },
        { key: 'retirementCoaching', label: 'Retirement Transitions' },
        { key: 'salesBusinessDevelopment', label: 'Sales / Business Development' },
        { key: 'stressManagement', label: 'Stress Management' },
        { key: 'successionPreparation', label: 'Succession Preparation' },
        { key: 'workLifeBalance', label: 'Work/Life Balance' },
        { key: 'workplaceRelationshipsInfluence', label: 'Workplace Relationships & Influence' },
        { key: OTHER_OPTION, label: 'Other Development Situations' },
      ],
    },
  },
  {
    id: COACHING_ACCREDITATIONS_FILTER_ID,
    label: 'Coaching Credentials & Professional Licensing',
    type: 'SelectMultipleFilter',
    group: 'secondary',
    queryParamNames: ['pub_coachingAccreditations'],
    config: {
      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: 'acCoach', label: 'AC - Coach' },
        { key: 'acCoachingSupervisor', label: 'AC - Coaching Supervisor' },
        { key: 'apecsAccreditedExecutiveCoach', label: 'APECS Accredited Executive Coach' },
        { key: 'apecsAccreditedExecutiveTeamCoach', label: 'APECS Accredited Executive Team Coach' },
        { key: 'apecsAccreditedSupervisor', label: 'APECS Accredited Supervisor' },
        { key: 'apecsFellow', label: 'APECS Fellow' },
        { key: 'apecsPractisingExecutiveCoach', label: 'APECS Practising Executive Coach' },
        { key: 'apecsPractisingExecutiveTeamCoach', label: 'APECS Practising Executive Team Coach' },
        { key: 'apecsPractisingSupervisor', label: 'APECS Practising Supervisor' },
        { key: 'cceBCC', label: 'CCE – BCC' },
        { key: 'degreeBAPsychology', label: 'Degree - BA in Psychology' },
        { key: 'degreeEMBA', label: 'Degree - EMBA' },
        { key: 'degreeExecMACoaching', label: 'Degree - Exec MA in Coaching' },
        { key: 'degreeGradCert', label: 'Degree - Grad Cert / Diploma in Coaching' },
        { key: 'degreeMACoachingPsychology', label: 'Degree - MA / MSc in Coaching Psychology' },
        { key: 'degreeMAOrgPsychology', label: 'Degree - MA in Org Psychology' },
        { key: 'degreeMAPsychology', label: 'Degree - MA in Psychology (other)' },
        { key: 'degreeMBA', label: 'Degree - MBA' },
        { key: 'degreePhDCoachingPsychology', label: 'Degree - Ph.D in Coaching Psychology' },
        { key: 'degreePhDOrgPsychology', label: 'Degree - Ph.D in Org Psychology' },
        { key: 'degreePhDPsychology', label: 'Degree - Ph.D in Psychology (other)' },
        { key: 'eascMember', label: 'EASC - Member' },
        { key: 'ecaAdvancedLevel', label: 'ECA - Advanced Level' },
        { key: 'ecaBasicLevel', label: 'ECA - Basic Level' },
        { key: 'ecaExpertLevel', label: 'ECA - Expert Level' },
        { key: 'emccEIA', label: 'EMCC - EIA' },
        { key: 'emccESIA', label: 'EMCC - ESIA' },
        { key: 'emccEIAFoundation', label: 'EMCC EIA – Foundation' },
        { key: 'emccEIAMaster', label: 'EMCC EIA – Master Practitioner' },
        { key: 'emccEIAPractitioner', label: 'EMCC EIA – Practitioner' },
        { key: 'emccEIASenior', label: 'EMCC EIA – Senior Practitioner' },
        { key: 'emccESIAMaster', label: 'EMCC ESIA – Master Practitioner' },
        { key: 'emccESIAPractitioner', label: 'EMCC ESIA – Practitioner' },
        { key: 'emccESIASenior', label: 'EMCC ESIA – Senior Practitioner' },
        { key: 'governmentSecurityClearance', label: 'Government Security Clearance' },
        { key: 'iacCMC', label: 'IAC - CMC' },
        { key: 'icfACC', label: 'ICF - ACC' },
        { key: 'icfMCC', label: 'ICF - MCC' },
        { key: 'icfPCC', label: 'ICF - PCC' },
        { key: 'icfACTC', label: 'ICF – ACTC' },
        { key: 'lapsed', label: 'Lapsed' },
        { key: 'licensedCounsellor', label: 'Licensed Counsellor' },
        { key: 'licensedPsychotherapist', label: 'Licensed Psychotherapist' },
        { key: 'licensedOrgPsychologist', label: 'Licensed/Registered Org Psychologist' },
        { key: 'licensedRegisteredPsychologistOther', label: 'Licensed/Registered Psychologist (other)' },
        { key: 'otherLicensedMentalHealthPractitioner', label: 'Other Licensed Mental Health Practitioner' },
        { key: 'qualificationsPending', label: 'Qualifications Pending' },
        { key: 'qualifiedExperience', label: 'Qualified by Experience' },
        { key: 'wabcCBC', label: 'WABC - CBC' },
        { key: 'wabcChBC', label: 'WABC - ChBC' },
        { key: 'wabcCMBC', label: 'WABC - CMBC' },
        { key: 'wabcRCC', label: 'WABC - RCC' },
        { key: OTHER_OPTION, label: 'Other' },
      ],
      isHideFromFilter: true,
    },
  },
  {
    id: ASSESSMENT_ACCREDITATIONS_FILTER_ID,
    label: 'Assessment Accreditations',
    type: 'SelectMultipleFilter',
    group: 'secondary',
    queryParamNames: ['pub_assessmentAccreditations'],
    config: {
      schemaType: 'multi-enum',
      searchMode: 'has_any',
      isHideFromFilter: true,
      // Gold Standard tools (⭐) are supported by substantial empirical research — strong reliability &
      // validity evidence, large normative datasets, published technical manuals, widely recognised in
      // organisational psychology, and defensible for professional coaching use from a governance
      // perspective with clear guidelines for ethical administration and qualified practitioner use.
      goldStandardNote:
        'Gold Standard assessments (⭐) are supported by substantial empirical research, with strong evidence of reliability and validity, large normative datasets, and published technical manuals. They are widely recognised in organisational psychology and are defensible for professional coaching use.',
      categories: [
        {
          key: 'groupTraitBased',
          label: 'Personality – Trait-Based (Big Five & Derivatives)',
          skills: [
            { key: 'hogan', label: '⭐ Hogan (HPI / HDS / MVPI)' },
            { key: 'personalityFactors', label: '⭐ 16PF' },
            { key: 'californiaPsychologicalInventory', label: '⭐ CPI / CPI 260' },
            { key: 'hexaco', label: '⭐ HEXACO' },
            { key: 'neoPIR', label: '⭐ NEO (NEO-PI-3 / NEO-PI-R)' },
            { key: 'opqSHL', label: '⭐ OPQ (SHL)' },
            { key: 'savilleWave', label: '⭐ Saville Wave' },
            { key: 'bfi2', label: 'BFI-2' },
            { key: 'caliper', label: 'Caliper' },
            { key: 'harrisonAssessments', label: 'Harrison Assessments' },
            { key: 'workplaceBigFive', label: 'Workplace Big Five' },
          ],
        },
        {
          key: 'groupTypeBased',
          label: 'Personality – Type / Preference-Based',
          skills: [
            { key: 'MBTI', label: 'MBTI' },
            { key: 'diSC', label: 'DiSC' },
            { key: 'enneagram', label: 'Enneagram' },
            { key: 'hbdi', label: 'HBDI' },
            { key: 'insights', label: 'Insights Discovery' },
            { key: 'luminaSpark', label: 'Lumina Spark' },
          ],
        },
        {
          key: 'groupEI',
          label: 'Emotional Intelligence',
          skills: [
            { key: 'theEQI2', label: '⭐ EQ-i 2.0' },
            { key: 'esci', label: '⭐ ESCi (Hay Group / Korn Ferry)' },
            { key: 'msceit', label: '⭐ MSCEIT (Ability-based EI)' },
            { key: 'genosEI', label: 'Genos EI' },
          ],
        },
        {
          key: 'groupLeadership',
          label: 'Leadership Capability & Culture (Non-Stage-Based)',
          skills: [
            { key: 'denisonLeadership', label: '⭐ Denison Leadership Development Survey' },
            { key: 'mindfulLeadership360', label: 'Mindful Leadership 360' },
          ],
        },
        {
          key: 'groupVertical',
          label: 'Vertical Development / Adult Development (Stage-Based)',
          skills: [
            { key: 'globalLeadershipProfile', label: '⭐ Global Leadership Profile (GLP) / MyWorldView' },
            { key: 'harthillLDP', label: '⭐ Harthill Leadership Development Profile (LDP)' },
            { key: 'verticalMindsetIndicator', label: 'Vertical Mindset Indicator (VMI) / Leadership Mindset Indicator (LMI)' },
            { key: 'shiftingHorizons', label: 'Shifting Horizons' },
          ],
        },
        {
          key: 'group360',
          label: '360 / Multi-Rater Feedback',
          skills: [
            { key: 'hogan360', label: '⭐ Hogan 360' },
            { key: 'leadershipCircleProfile', label: '⭐ Leadership Circle Profile' },
            { key: 'lsi360', label: '⭐ Life Styles Inventory (LSI-360)' },
            { key: 'shl360', label: '⭐ SHL 360' },
            { key: 'custom360', label: 'Custom 360' },
          ],
        },
        {
          key: 'groupTeam',
          label: 'Team & Interpersonal',
          skills: [
            { key: 'teamManagementProfile', label: '⭐ Team Management Profile (TMP)' },
            { key: 'belbinTeamRoles', label: '⭐ Belbin Team Roles' },
            { key: 'FIROB', label: '⭐ FIRO-B' },
            { key: 'strengthDeploymentInventory', label: 'Strength Deployment Inventory (SDI)' },
            { key: 'brite', label: 'BRITE' },
            { key: 'groupStylesInventory', label: 'Group Styles Inventory (Human Synergistics)' },
            { key: 'hbdiTeamDashboard', label: 'HBDI Team Effectiveness Dashboard' },
            { key: 'hpta', label: 'HPTA' },
            { key: 'thomasKilmannConflictModeInstrument', label: 'Thomas-Kilmann Conflict Mode Instrument (TKI)' },
          ],
        },
        {
          key: 'groupStrengths',
          label: 'Strengths & Values',
          skills: [
            { key: 'cliftonStrengths', label: '⭐ CliftonStrengths' },
            { key: 'viaStrengths', label: '⭐ VIA Character Strengths' },
            { key: 'strengthsProfiler', label: 'StrengthsProfiler' },
            { key: 'viaEdge', label: 'viaEdge' },
          ],
        },
        {
          key: 'groupCareer',
          label: 'Career & Interests',
          skills: [
            { key: 'strongInterestInventory', label: '⭐ Strong Interest Inventory' },
          ],
        },
        {
          key: 'groupResilience',
          label: 'Resilience / Wellbeing / Mental Toughness',
          skills: [
            { key: 'mtq48', label: '⭐ MTQ48++' },
            { key: 'cdRISC', label: '⭐ Connor-Davidson Resilience Scale (CD-RISC)' },
            { key: 'aura', label: 'AURA' },
            { key: 'gpsi', label: 'GPSi' },
          ],
        },
        {
          key: 'groupOther',
          label: 'Other',
          skills: [
            { key: OTHER_OPTION, label: 'Other' },
          ],
        },
      ],
    },
  },
  {
    id: COACHING_EXPERIENCE_BY_CAREER_PHASE_FILTER_ID,
    label: 'Coaching Experience by Career Phase',
    type: 'SelectMultipleFilter',
    group: 'secondary',
    queryParamNames: ['pub_coachingExperienceByCareerPhase'],
    config: {
      schemaType: 'multi-enum',
      searchMode: 'has_any',
      options: [
        { key: 'earlyCareer', label: 'Early career' },
        { key: 'midCareerManagement', label: 'Mid career - management' },
        {
          key: 'midCareerSubjectMatterExpert',
          label: 'Mid career - non-management',
        },
        { key: 'subjectMatterExpert', label: 'Subject Matter Expert' },
        { key: 'leadership', label: 'Leadership' },
        { key: 'executiveSME', label: 'Executive - SME' },
        { key: 'executivePLC', label: 'Executive - PLC' },
        { key: 'executiveConglomerate', label: 'Executive - Conglomerate' },
        { key: 'executivePrivateLargeOrg', label: 'Executive - Private Large Org' },
        { key: 'board', label: 'Board' },
      ],
      isHideFromFilter: true,
    },
  },
  {
    id: HR_STRATEGIC_SKILLS_FILTER_ID,
    label: "Consulting Skills",
    type: 'SelectMultipleFilter',
    group: 'secondary',
    queryParamNames: ['pub_hrStrategicSkills'],
    config: {
      schemaType: 'multi-enum',
      searchMode: 'has_any',
      isHideFromFilter: true,
      categories: [
        {
          key: 'facilitationOffsites',
          label: 'Facilitation & Offsites',
          skills: [
            { key: 'workshopContentDesign', label: 'Workshop Content Design' },
            { key: 'eltOffsiteFacilitation', label: 'ELT Offsite Facilitation' },
            { key: 'eltOffsiteExperienceDesign', label: 'ELT Offsite Experience Design' },
            { key: 'workshopFacilitation', label: 'Workshop Facilitation' },
          ],
        },
        {
          key: 'workforceStrategyPlanning',
          label: 'Workforce Strategy & Planning',
          skills: [
            {
              key: 'strategicWorkforcePlanningReviews',
              label: 'Strategic Workforce Planning / Reviews',
            },
            {
              key: 'futureOfWorkScenarioPlanning',
              label: 'Future of Work / Scenario Planning',
            },
            { key: 'workforceAnalyticsForecasting', label: 'Workforce Analytics & Forecasting' },
            { key: 'skillsBasedWorkforcePlanning', label: 'Skills-Based Workforce Planning' },
            { key: 'workfoceAICapabilityPlanning', label: 'Workfoce AI Capability Planning' },
            { key: 'skillsTaxonomyDesign', label: 'Skills Taxonomy Design' },
          ],
        },
        {
          key: 'organisationDesignArchitecture',
          label: 'Organisation Design & Architecture',
          skills: [
            {
              key: 'organisationDesignRestructuring',
              label: 'Organisation Design & Restructuring',
            },
            { key: 'organisationalArchitecture', label: 'Organisational Architecture' },
            { key: 'operatingModelDesign', label: 'Operating Model Design' },
            { key: 'roleJobArchitecture', label: 'Role & Job Architecture' },
            { key: 'governanceDecisionRights', label: 'Governance & Decision Rights' },
            { key: 'organisationEffectiveness', label: 'Organisation Effectiveness' },
            {
              key: 'organisationNetworkAnalysisONA',
              label: 'Organisation Network Analysis (ONA)',
            },
            { key: 'jobFamilyArchitecture', label: 'Job Family Architecture' },
          ],
        },
        {
          key: 'changeCultureTransformation',
          label: 'Change, Culture & Transformation',
          skills: [
            { key: 'changeManagement', label: 'Change Management' },
            { key: 'changeCommunications', label: 'Change Communications' },
            { key: 'cultureTransformation', label: 'Culture Transformation' },
            { key: 'transformationProgrammeDesign', label: 'Transformation Programme Design' },
            {
              key: 'enterprisePsychosocialSafetyStrategy',
              label: 'Enterprise Psycho-social Safety Strategy',
            },
            { key: 'cultureMeasurementDiagnostics', label: 'Culture Measurement / Diagnostics' },
            { key: 'changeAdoptionMeasurement', label: 'Change Adoption Measurement' },
            { key: 'strategicNarrativeDevelopment', label: 'Strategic Narrative Development' },
          ],
        },
        {
          key: 'leadershipCapability',
          label: 'Leadership & Capability',
          skills: [
            { key: 'leadershipDevelopmentScience', label: 'Leadership Development Science' },
            { key: 'highPotentialDevelopment', label: 'High-Potential Development' },
            { key: 'successionPlanning', label: 'Succession Planning' },
            { key: 'capabilityFrameworks', label: 'Capability Frameworks' },
            {
              key: 'capabilityAssessmentNonPsychometric',
              label: 'Capability Assessment (non-psychometric)',
            },
            { key: 'learningStrategyArchitecture', label: 'Learning Strategy & Architecture' },
            { key: 'behaviourChangeScience', label: 'Behaviour Change Science' },
            { key: 'managerDevelopmentScience', label: 'Manager Development Science' },
            { key: 'executiveTeamEffectiveness', label: 'Executive Team Effectiveness' },
            { key: 'boardEffectivenessReviews', label: 'Board Effectiveness Reviews' },
            { key: 'leadershipAssessmentDesign', label: 'Leadership Assessment Design' },
            {
              key: 'facilitationOfLeadershipAlignment',
              label: 'Facilitation of Leadership Alignment',
            },
          ],
        },
        {
          key: 'talentManagement',
          label: 'Talent Management',
          skills: [
            { key: 'talentStrategy', label: 'Talent Strategy' },
            {
              key: 'performanceManagementProgramDesign',
              label: 'Performance Management Program Design',
            },
            { key: 'talentReviewProcessDesign', label: 'Talent Review Process Design' },
            { key: 'okrProgramDesign', label: 'OKR Program Design' },
            { key: 'internalMobilityDesign', label: 'Internal Mobility Design' },
            {
              key: 'diversityInclusionBelongingStrategy',
              label: 'Diversity, Inclusion & Belonging Strategy',
            },
            { key: 'mentoringProgramDesign', label: 'Mentoring Program Design' },
            { key: 'coachingCultureStrategy', label: 'Coaching Culture Strategy' },
            { key: 'bespoke360AssessmentDesign', label: 'Bespoke 360 Assessment Design' },
          ],
        },
        {
          key: 'talentAcquisition',
          label: 'Talent Acquisition',
          skills: [
            { key: 'talentAcquisitionStrategy', label: 'Talent Acquisition Strategy' },
            { key: 'employerValuePropositionEVP', label: 'Employer Value Proposition (EVP)' },
            { key: 'candidateExperienceDesign', label: 'Candidate Experience Design' },
            { key: 'diversityHiringStrategy', label: 'Diversity Hiring Strategy' },
          ],
        },
        {
          key: 'engagementTalentExperience',
          label: 'Engagement & Talent Experience',
          skills: [
            { key: 'employeeEngagementStrategy', label: 'Employee Engagement Strategy' },
            { key: 'employeeExperienceEXDesign', label: 'Employee Experience (EX) Design' },
            { key: 'wellnessBenefitsDesign', label: 'Wellness & Benefits Design' },
            { key: 'valuesPurposeDevelopment', label: 'Values & Purpose Development' },
            { key: 'teamPsychologicalSafetyInitiatives', label: 'Team Psychological Safety Initiatives' },
            {
              key: 'engagementConsultationMethods',
              label: 'Engagement & Consultation Methods (Surveys, Focus Groups)',
            },
          ],
        },
        {
          key: 'rewardPayRecognition',
          label: 'Reward, Pay & Recognition',
          skills: [
            { key: 'totalRewardsStrategy', label: 'Total Rewards Strategy' },
            { key: 'compensationBenchmarking', label: 'Compensation Benchmarking' },
            { key: 'esopRsuPlanDesign', label: 'ESOP & RSU Plan Design' },
            { key: 'jobEvaluation', label: 'Job Evaluation' },
            { key: 'payEquityReviews', label: 'Pay Equity Reviews' },
            { key: 'recognitionProgrammes', label: 'Recognition Programmes' },
          ],
        },
        {
          key: 'peopleAnalyticsInsights',
          label: 'People Analytics & Insights',
          skills: [
            { key: 'peopleAnalyticsStrategy', label: 'People Analytics Strategy' },
            { key: 'hrDashboardsReporting', label: 'HR Dashboards & Reporting' },
            { key: 'dataAnalysisQualitative', label: 'Data Analysis (Qualitative)' },
            { key: 'dataAnalysisQuantitative', label: 'Data Analysis (Quantitative)' },
            { key: 'dataVisualisationReporting', label: 'Data Visualisation & Reporting' },
            { key: 'predictiveAnalytics', label: 'Predictive Analytics' },
          ],
        },
        {
          key: 'hrOperatingModelSystems',
          label: 'HR Operating Model & Systems',
          skills: [
            { key: 'hrOperatingModelDesign', label: 'HR Operating Model Design' },
            { key: 'hrTransformation', label: 'HR Transformation' },
            { key: 'hrSystemsDesign', label: 'HR Systems Design' },
            {
              key: 'hrTechnologyStrategyHRISATSLMS',
              label: 'HR Technology Strategy (HRIS, ATS, LMS)',
            },
            { key: 'digitalHRTransformation', label: 'Digital HR Transformation' },
            { key: 'hrProcessDesign', label: 'HR Process Design' },
            { key: 'sharedServicesCoeeBPModel', label: 'Shared Services / CoE / BP Model' },
            { key: 'serviceDeliveryStrategy', label: 'Service Delivery Strategy' },
            { key: 'aiTransformationForHRServices', label: 'AI Transformation for HR Services' },
          ],
        },
        {
          key: 'governanceRiskPolicy',
          label: 'Governance, Risk & Policy',
          skills: [
            { key: 'hrGovernance', label: 'HR Governance' },
            {
              key: 'governanceReviewsBoardEnterprise',
              label: 'Governance Reviews (Board / Enterprise)',
            },
            { key: 'policyDesign', label: 'Policy Design' },
            { key: 'employmentRiskManagement', label: 'Employment Risk Management' },
            { key: 'regulatoryCompliance', label: 'Regulatory Compliance' },
            { key: 'dataGovernance', label: 'Data Governance' },
            { key: 'aiGovernanceForPeopleFunctions', label: 'AI Governance for People Functions' },
          ],
        },
        {
          key: 'strategyBusinessAdvisory',
          label: 'Strategy, Business & Advisory',
          skills: [
            { key: 'businessModelReview', label: 'Business Model Review' },
            { key: 'businessStrategyPlanning', label: 'Business Strategy / Planning' },
            {
              key: 'enterpriseWideAIReadinessAssessments',
              label: 'Enterprise-wide AI Readiness Assessments',
            },
            { key: 'executiveStakeholderInterviewing', label: 'Executive / Stakeholder Interviewing' },
          ],
        },
        {
          key: 'maRestructuringSpecialSituations',
          label: 'M&A, Restructuring & Special Situations',
          skills: [
            { key: 'maAdvisoryPreMerger', label: 'M&A Advisory (Pre-Merger)' },
            { key: 'postMergerIntegrationPeople', label: 'Post-Merger Integration (People)' },
            { key: 'restructuringDownsizing', label: 'Restructuring / Downsizing' },
            {
              key: 'industrialRelationsUnionEngagement',
              label: 'Industrial Relations / Union Engagement',
            },
          ],
        },
        {
          key: 'designInnovation',
          label: 'Design & Innovation',
          skills: [
            { key: 'designOfElearningResources', label: 'Design of E-learning resources' },
            { key: 'actionLearningDesign', label: 'Action Learning Design' },
            {
              key: 'designOfSimulationsExperientialLearning',
              label: 'Design of Simulations & Experiential Learning',
            },
            {
              key: 'roleExcellenceMappingForLearningPathways',
              label: 'Role Excellence Mapping for Learning Pathways',
            },
          ],
        },
        {
          key: 'programmeProjectDelivery',
          label: 'Programme & Project Delivery',
          skills: [
            { key: 'programmeEvaluation', label: 'Programme Evaluation' },
            { key: 'projectManagementLargeComplex', label: 'Project Management (Large / Complex)' },
          ],
        },
        {
          key: 'research',
          label: 'Research',
          skills: [
            {
              key: 'academicGradeResearchBehaviouralSciences',
              label: 'Academic-grade research - behavioural sciences',
            },
            { key: 'journalSubmissionManagement', label: 'Journal submission management' },
          ],
        },
      ],
    },
  },
];

export const sortConfig = {
  // Enable/disable the sorting control in the SearchPage
  active: true,

  // Note: queryParamName 'sort' is fixed,
  // you can't change it since Flex API expects it to be named as 'sort'
  queryParamName: 'sort',

  // Internal key for the relevance option, see notes below.
  relevanceKey: 'relevance',

  // Relevance key is used with keywords filter.
  // Keywords filter also sorts results according to relevance.
  relevanceFilter: 'keywords',

  // Keyword filter is sorting the results by relevance.
  // If keyword filter is active, one might want to disable other sorting options
  // by adding 'keyword' to this list.
  conflictingFilters: [],

  options: [
    { key: 'createdAt', label: 'Newest' },
    { key: '-createdAt', label: 'Oldest' },
    { key: '-price', label: 'Lowest price' },
    { key: 'price', label: 'Highest price' },

    // The relevance is only used for keyword search, but the
    // parameter isn't sent to the Marketplace API. The key is purely
    // for handling the internal state of the sorting dropdown.
    { key: 'relevance', label: 'Relevance', longLabel: 'Relevance (Keyword search)' },
  ],
};

export const BUYER_ROLE = 'buyer';
export const COACH_ROLE = 'coach';
export const COACHEE_ROLE = 'coachee';
export const ADMIN_ROLE = 'admin';
export const GUEST_ROLE = 'guest'; //non login user
export const userFilters = [
  {
    id: 'role',
    label: 'Role',
    config: {
      options: [
        { key: BUYER_ROLE, label: 'Looking for a coach' },
        { key: COACH_ROLE, label: 'A coach' },
      ],
    },
  },
];

export const paymentMethodsFilters = [
  {
    id: PAYMENT_METHODS_FILTER_ID,
    label: 'Payment methods',
    config: {
      options: [{ key: 'invoice', label: 'Invoice' }, { key: 'creditCard', label: 'Credit Card' }],
    },
  },
];

export const careerPhaseOptions = [
  { key: 'earlyCareer', label: 'Early career' },
  { key: 'management', label: 'Mid career - management' },
  { key: 'nonManagement', label: 'Mid career - non-management' },
  { key: 'subjectMatterExpert', label: 'Subject Matter Expert' },
  { key: 'leadership', label: 'Leadership' },
  { key: 'executive', label: 'Executive' },
];

export const coachingFormatOptionsForDropdown = [
  { key: 'online', label: 'Online' },
  { key: 'faceToFace', label: 'Face to face' },
  { key: 'both', label: 'Either Online or Face to Face' },
];

export const YES_OPTION_KEY = 'yes';
export const NO_OPTION_KEY = 'no';
export const yesNoOptions = [
  { key: YES_OPTION_KEY, label: 'Yes' },
  { key: NO_OPTION_KEY, label: 'No' },
];

export const MOST_RECENT_BLOGS = 'most_recent';
export const COACHING_BLOGS = 'coaching';
export const COACHING_CULTURE_BLOGS = 'coaching_culture';
export const SHORT_LIST_TAB = 'short-list';
export const CHEMISTRY_TAB = 'chemistry';
export const CHEMISTRY_SESSION_LIMIT = 3;
export const MOST_RECENT_CASE_STUDY = 'most_recent';
export const COACHING_CASE_STUDY = 'coaching';
export const COACHING_CULTURE_CASE_STUDY = 'coaching_culture';

export const accountStatus = {
  waiting: 'waiting',
  claim: 'claim',
};

export const optInValueMaybe = {
  checked: true,
  unchecked: false,
};

export const INTERESTED_ENGAGEMENT = 'interested';
export const NOT_INTERESTED_ENGAGEMENT = 'notInterested';

// Knowledge Hub's article type
export const FOR_CLIENT_ARTICLE = 'for_client';
export const FOR_COACH_ARTICLE = 'for_coach';
export const FOR_COACHEE_ARTICLE = 'for_coachee';

export const knowledgeHubArticles = [
  { key: FOR_CLIENT_ARTICLE, label: 'For Client' },
  { key: FOR_COACH_ARTICLE, label: 'For Coach' },
  { key: FOR_COACHEE_ARTICLE, label: 'For Coachee' },
];

export const REQUIRED_LABEL = '*';

// ─── HR / Strategic Talent Consulting Skills ─────────────────────────────────
// Derived from filters config — single source of truth
export const HR_STRATEGIC_SKILL_CATEGORIES = filters.find(
  f => f.id === HR_STRATEGIC_SKILLS_FILTER_ID
).config.categories;

// ─── Assessment Accreditations ───────────────────────────────────────────────
// Derived from filters config — single source of truth
export const ASSESSMENT_ACCREDITATION_CATEGORIES = filters.find(
  f => f.id === ASSESSMENT_ACCREDITATIONS_FILTER_ID
).config.categories;
